import React, { useEffect, useState } from 'react';
import {
  Box, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox, Toolbar, Chip,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  DigitalQuestion, DigitalSection, DigitalSurvey, Trial,
} from '../../../redux/types';
import ApiClient from '../../../loopback/apiClient';
import ZerpaSearch from '../../layout/ZerpaSearch';
import { getYearsApartInArray } from '../../utilities/Sorting';
import FilterSelect from '../../trial/FilterSelect';

type SectionData = {
  display: boolean;
  sections: DigitalSection[];
  questions: DigitalQuestion[];
}
type SurveyData = Record<number, SectionData>;

type TrialListType = Trial & {
  digitalSurveys: DigitalSurvey[];
}
type Props = {
  selectedSections: Map<number, DigitalSection>;
  onSectionSelect: (
    event: React.ChangeEvent<HTMLInputElement>,
    section: DigitalSection,
    questions: DigitalQuestion[],
  ) => void;
  currentTrialId?: number;
};

const SectionCopySelector: React.FC<Props> = ({
  selectedSections,
  onSectionSelect,
  currentTrialId,
}: Props) => {
  const [trialList, setTrialList] = useState<TrialListType[]>();
  const [filteredTrialList, setfilteredTrialList] = useState<TrialListType[]>();
  const [surveyCache, setSurveyCache] = useState<SurveyData>(Object.create(null));
  const [searchText, setSearchText] = useState<string>('');
  const [year, setYear] = useState<string>(new Date().getFullYear().toString());

  useEffect(() => {
    ApiClient.getTrialsV2().then((res) => setTrialList(res));
  }, []);

  useEffect(() => {
    if (!trialList) {
      return;
    }
    let filteredData = trialList;
    if (year) {
      filteredData = filteredData.filter((val) => val.startDate.split('-')[0] === year);
    }
    if (searchText) {
      const trimmedSearchText = searchText.toLowerCase().trim();
      filteredData = filteredData.filter((val) => (
        val.name.toLowerCase().includes(trimmedSearchText)
        || val.code.toLowerCase().includes(trimmedSearchText)
        || val.id.toString().includes(trimmedSearchText)
      ));
    }
    setfilteredTrialList(filteredData);
  }, [searchText, trialList, year]);

  const fetchSurveySections = async (surveyId: number) => {
    const extendedSurvey = await ApiClient.fetchDigitalSurvey(surveyId);
    setSurveyCache({
      ...surveyCache,
      [surveyId]: {
        display: true,
        sections: extendedSurvey.digitalSections,
        questions: extendedSurvey.digitalQuestions,
      },
    });
  };

  const toggleList = (surveyId: number) => {
    if (Object.prototype.hasOwnProperty.call(surveyCache, surveyId)) {
      setSurveyCache({
        ...surveyCache,
        [surveyId]: {
          ...surveyCache[surveyId],
          display: !surveyCache[surveyId].display,
        },
      });
    } else {
      fetchSurveySections(surveyId);
    }
  };

  const onSearchTextChange = (searchTxt: string) => {
    setSearchText(searchTxt);
  };

  if (!trialList || !filteredTrialList) {
    return null;
  }

  return (
    <Box>
      <Toolbar sx={{ alignItems: 'flex-end', bgcolor: 'grey.100', py: 1 }}>
        <ZerpaSearch fullWidth onTextChange={onSearchTextChange} value={searchText} />
        <FilterSelect
          id="TrialSearchType-yearselect"
          options={getYearsApartInArray()}
          value={year}
          label={<FormattedMessage id="trialSearch.yearSelectHeading" />}
          onChange={(selectedYear) => setYear(selectedYear)}
        />
      </Toolbar>
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {filteredTrialList.filter((v) => v.digitalSurveys[0]).map((t) => {
          const labelId = `checkbox-list-secondary-label-${t.name}`;
          return (
            <React.Fragment key={t.id}>
              <ListItem
                disablePadding
              >
                <ListItemButton onClick={() => toggleList(t.digitalSurveys[0].id)}>
                  <ListItemIcon>
                    {
                      surveyCache[t.digitalSurveys[0].id]
                      && surveyCache[t.digitalSurveys[0].id].display
                        ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={t.name} />
                  { t.id === currentTrialId
                    && (<Chip label="current" color="primary" size="small" />)}
                </ListItemButton>
              </ListItem>
              <Divider />
              <Box>
                {
                  surveyCache[t.digitalSurveys[0].id]
                  && surveyCache[t.digitalSurveys[0].id].display && (
                    <List dense sx={{ pl: 2.5 }}>
                      {
                        surveyCache[t.digitalSurveys[0].id].sections
                          .sort((a, b) => a.sortIndex - b.sortIndex)
                          .map((s) => (
                            <React.Fragment key={s.id}>
                              <ListItem
                                dense
                                disablePadding
                              >
                                <ListItemIcon>
                                  <Checkbox
                                    checked={selectedSections.has(s.id)}
                                    onChange={
                                      (e) => onSectionSelect(
                                        e, s, surveyCache[t.digitalSurveys[0].id].questions,
                                      )
                                    }
                                  />
                                </ListItemIcon>
                                <ListItemText id={labelId} secondary={s.name} />
                              </ListItem>
                            </React.Fragment>
                          ))
                      }
                    </List>
                  )
                }
              </Box>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default SectionCopySelector;
